import { Facebook, Twitter, Share2 } from 'lucide-react';

// Brand SVGs for platforms that are not in lucide-react.
const WhatsAppIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.206-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const MessengerIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 0C5.373 0 0 4.975 0 11.111c0 3.497 1.745 6.616 4.472 8.652V24l4.086-2.242c1.09.301 2.246.464 3.442.464 6.627 0 12-4.974 12-11.111C24 4.975 18.627 0 12 0zm1.191 14.963l-3.055-3.26-5.963 3.26 6.559-6.963 3.131 3.26 5.889-3.26-6.561 6.963z" />
  </svg>
);

const buttonBase =
  'w-8 h-8 rounded-full flex items-center justify-center transition-all hover:scale-110 hover:shadow-md';

/**
 * Social share buttons for a single card/post.
 * Uses native Web Share API when available (mobile-friendly, includes Instagram),
 * with platform-specific fallbacks for desktop.
 */
const SocialShareButtons = ({
  url,
  title = '',
  testIdPrefix = 'share',
  className = '',
}) => {
  const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  const shareText = title ? `${title} — ${shareUrl}` : shareUrl;
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(title);
  const encodedText = encodeURIComponent(shareText);

  const supportsNativeShare =
    typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const handleNativeShare = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.share({ title, text: title, url: shareUrl });
    } catch (err) {
      // User dismissed; ignore silently
    }
  };

  const openShare = (e, shareHref) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(shareHref, '_blank', 'noopener,noreferrer,width=600,height=500');
  };

  const actions = [
    ...(supportsNativeShare
      ? [
          {
            key: 'native',
            label: 'Dela',
            classes: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
            onClick: handleNativeShare,
            icon: <Share2 className="w-4 h-4" />,
          },
        ]
      : []),
    {
      key: 'facebook',
      label: 'Facebook',
      classes: 'bg-[#1877F2] text-white hover:bg-[#0f63cc]',
      onClick: (e) =>
        openShare(e, `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`),
      icon: <Facebook className="w-4 h-4" fill="currentColor" />,
    },
    {
      key: 'whatsapp',
      label: 'WhatsApp',
      classes: 'bg-[#25D366] text-white hover:bg-[#1da851]',
      onClick: (e) => openShare(e, `https://wa.me/?text=${encodedText}`),
      icon: <WhatsAppIcon className="w-4 h-4" />,
    },
    {
      key: 'messenger',
      label: 'Messenger',
      classes: 'bg-[#0099FF] text-white hover:bg-[#007acc]',
      onClick: (e) => {
        e.preventDefault();
        e.stopPropagation();
        // fb-messenger deep-link works on mobile devices with Messenger installed.
        // On desktop the navigation silently fails, matching the native-share paradigm.
        window.location.href = `fb-messenger://share?link=${encodedUrl}`;
      },
      icon: <MessengerIcon className="w-4 h-4" />,
    },
    {
      key: 'twitter',
      label: 'X (Twitter)',
      classes: 'bg-black text-white hover:bg-gray-800',
      onClick: (e) =>
        openShare(
          e,
          `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
        ),
      icon: <Twitter className="w-4 h-4" fill="currentColor" />,
    },
  ];

  return (
    <div
      className={`flex flex-wrap items-center gap-2 ${className}`}
      data-testid={`${testIdPrefix}-buttons`}
      onClick={(e) => e.stopPropagation()}
    >
      {actions.map((action) => (
        <button
          key={action.key}
          type="button"
          aria-label={`Dela via ${action.label}`}
          title={action.label}
          onClick={action.onClick}
          className={`${buttonBase} ${action.classes}`}
          data-testid={`${testIdPrefix}-${action.key}`}
        >
          {action.icon}
        </button>
      ))}
    </div>
  );
};

export default SocialShareButtons;
